import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_DMC_PROVIDER_LIST_CONFIGURATION, EMPTY_DMC_PROVIDER_LIST_CRITERIA } from "../constant/dmcProviderConstant";
import { DmcProviderListCriteria } from "../objects/dmcProviderListCriteria";
import { DmcProviderState } from "../objects/dmcProviderState";
import { setDmcProviderPreference } from "../utils/setDmcProviderPreference";
import { CustomProvider } from "../../customProvider/objects/customProvider";


const initialState: DmcProviderState = {
    dmc_provider_list: [],
    selected_dmc_provider: [],
    refresh_list: false,
    dmc_provider_list_configuration: EMPTY_DMC_PROVIDER_LIST_CONFIGURATION,
    dmc_provider_list_loading: false,
    dmc_provider_list_count: 0,
    dmc_provider_list_criteria: EMPTY_DMC_PROVIDER_LIST_CRITERIA,
    first_preference_loaded: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');


const setSelectedDmcProviderReducer = (state: DmcProviderState, action: PayloadAction<CustomProvider[]>): DmcProviderState => ({
    ...state, selected_dmc_provider: action.payload
});

const toggleRefreshListReducer = (state: DmcProviderState): DmcProviderState => ({
    ...state, refresh_list: !state.refresh_list
});

const addDmcProviderListConfigurationPreferenceReducer = (state: DmcProviderState, action: PayloadAction<Preferences[]>): DmcProviderState => ({
    ...state, dmc_provider_list_configuration: { ...state.dmc_provider_list_configuration, preferences: [...state.dmc_provider_list_configuration.preferences, ...action.payload] }
});

const setDmcProviderListConfigurationPreferenceReducer = (state: DmcProviderState, action: PayloadAction<Preferences[]>): DmcProviderState => ({
    ...state, dmc_provider_list_configuration: { ...state.dmc_provider_list_configuration, preferences: action.payload }
});


const setPreferenceReducer = (state: DmcProviderState, action: PayloadAction<Preferences[]>): DmcProviderState => {
    const { preferences, columns, order_by, order } = setDmcProviderPreference(action.payload, state.dmc_provider_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        dmc_provider_list_configuration: { ...state.dmc_provider_list_configuration, preferences, columns },
        dmc_provider_list_criteria: { ...state.dmc_provider_list_criteria, order, order_by }
    };
};

const moveDownColumnReducer = (state: DmcProviderState, action: PayloadAction<number>): DmcProviderState => {
    const columns = cloneDeep([...state.dmc_provider_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, dmc_provider_list_configuration: { ...state.dmc_provider_list_configuration, columns } };
};


const moveUpColumnReducer = (state: DmcProviderState, action: PayloadAction<number>): DmcProviderState => {
    const columns = cloneDeep([...state.dmc_provider_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, dmc_provider_list_configuration: { ...state.dmc_provider_list_configuration, columns } };
};

const toggleColumnReducer = (state: DmcProviderState, action: PayloadAction<number>): DmcProviderState => {
    const columns = cloneDeep([...state.dmc_provider_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, dmc_provider_list_configuration: { ...state.dmc_provider_list_configuration, columns } };
};

const updateDmcProviderListReducer = (state: DmcProviderState, action: PayloadAction<CustomProvider[]>): DmcProviderState => ({
    ...state, dmc_provider_list: action.payload
});
const updateDmcProviderListCriteriaReducer = (state: DmcProviderState, action: PayloadAction<DmcProviderListCriteria>): DmcProviderState => ({
    ...state, dmc_provider_list_criteria: action.payload
});
const updateDmcProviderListLoadingReducer = (state: DmcProviderState, action: PayloadAction<boolean>): DmcProviderState => ({
    ...state, dmc_provider_list_loading: action.payload
});
const updateDmcProviderListCountReducer = (state: DmcProviderState, action: PayloadAction<number>): DmcProviderState => ({
    ...state, dmc_provider_list_count: action.payload
});

export const dmcProvider = createSlice({
    name: 'dmc_provider',
    initialState,
    reducers: {
        setSelectedDmcProvider: setSelectedDmcProviderReducer,
        toggleRefreshList: toggleRefreshListReducer,
        addDmcProviderListConfigurationPreference: addDmcProviderListConfigurationPreferenceReducer,
        setDmcProviderListConfigurationPreference: setDmcProviderListConfigurationPreferenceReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        toggleColumn: toggleColumnReducer,
        updateDmcProviderList: updateDmcProviderListReducer,
        updateDmcProviderListCriteria: updateDmcProviderListCriteriaReducer,
        updateDmcProviderListLoading: updateDmcProviderListLoadingReducer,
        updateDmcProviderListCount: updateDmcProviderListCountReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setSelectedDmcProvider,
    toggleRefreshList,
    addDmcProviderListConfigurationPreference,
    setDmcProviderListConfigurationPreference,
    moveDownColumn,
    moveUpColumn,
    toggleColumn,
    updateDmcProviderList,
    updateDmcProviderListCriteria,
    updateDmcProviderListLoading,
    updateDmcProviderListCount
} = dmcProvider.actions;